import {
  MouseEvent as ReactMouseEvent,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useEvent, useRafState, useToggle } from 'react-use';

interface UseResizablePanelOptions {
  minWidth: number;
  /** Minimum width that must remain available to the content on the other side of the divider. */
  minRemainingWidth?: number;
  /** Maximum width of the left panel. When not supplied, the panel can grow to fill the container minus `minWidth`. */
  maxWidth?: number;
  /** Default width in pixels. Falls back to half of the content width when not supplied. */
  defaultWidth?: number;
  /** Ref to the row container so we can derive max-width dynamically. */
  containerRef?: RefObject<HTMLDivElement | null>;
  /** Width reserved inside the container for non-panel elements like the resize handle. */
  reservedWidth?: number;
}

const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max);

const FALLBACK_MAX_WIDTH = 1200;

export function useResizablePanel({
  minWidth,
  minRemainingWidth = minWidth,
  maxWidth: maxWidthProp,
  defaultWidth: defaultWidthProp,
  containerRef,
  reservedWidth = 0,
}: UseResizablePanelOptions) {
  // Resolve a sensible default: use the explicit prop, or derive from the container.
  const resolveDefault = useCallback(() => {
    if (defaultWidthProp !== undefined) return defaultWidthProp;
    const containerWidth = containerRef?.current?.clientWidth ?? 0;
    return containerWidth > 0
      ? Math.round((containerWidth - reservedWidth) / 2)
      : 600;
  }, [defaultWidthProp, containerRef, reservedWidth]);

  const resolveMax = useCallback(() => {
    if (maxWidthProp !== undefined) return maxWidthProp;
    const containerWidth = containerRef?.current?.clientWidth ?? 0;

    if (containerWidth <= 0) {
      return FALLBACK_MAX_WIDTH;
    }

    return Math.max(
      minWidth,
      containerWidth - minRemainingWidth - reservedWidth
    );
  }, [maxWidthProp, containerRef, minRemainingWidth, minWidth, reservedWidth]);

  const [panelWidth, setPanelWidthState] = useRafState(resolveDefault);
  const panelWidthRef = useRef(panelWidth);
  const setPanelWidth = useCallback(
    (nextWidth: SetStateAction<number>) => {
      const resolvedWidth =
        typeof nextWidth === 'function'
          ? nextWidth(panelWidthRef.current)
          : nextWidth;
      panelWidthRef.current = resolvedWidth;
      setPanelWidthState(resolvedWidth);
    },
    [setPanelWidthState]
  );
  const [isResizing, toggleResizing] = useToggle(false);
  const resizeRef = useRef<HTMLDivElement>(null);

  const clampPanelWidth = useCallback(
    (nextWidth: number) => clamp(nextWidth, minWidth, resolveMax()),
    [minWidth, resolveMax]
  );

  // Once the container is measured, initialize from the measured width and
  // keep the current width clamped as the available space changes.
  const hasInitialized = useRef(false);
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const syncPanelWidth = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth <= 0) return;

      if (!hasInitialized.current) {
        hasInitialized.current = true;
        setPanelWidth(clampPanelWidth(resolveDefault()));
        return;
      }

      setPanelWidth((prevWidth) => clampPanelWidth(prevWidth));
    };

    syncPanelWidth();

    const observer = new ResizeObserver(syncPanelWidth);
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [clampPanelWidth, containerRef, resolveDefault, setPanelWidth]);

  useEffect(() => {
    if (containerRef) return;
    setPanelWidth((prevWidth) => clampPanelWidth(prevWidth));
  }, [clampPanelWidth, containerRef, setPanelWidth]);

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      toggleResizing(true);
    },
    [toggleResizing]
  );

  useEvent(
    'mousemove',
    (e: Event) => {
      if (!isResizing) return;
      const mouseEvent = e as MouseEvent;
      const containerLeft =
        containerRef?.current?.getBoundingClientRect().left ?? 0;
      setPanelWidth(clampPanelWidth(mouseEvent.clientX - containerLeft));
    },
    document
  );

  useEvent(
    'mouseup',
    () => {
      if (isResizing) {
        toggleResizing(false);
      }
    },
    document
  );

  useEffect(() => {
    if (!isResizing) return;
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isResizing]);

  return {
    panelWidth,
    isResizing,
    resizeRef,
    handleMouseDown,
  };
}
